import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout, selectUser } from "../store/authSlice";

// ---- API endpoints this screen will eventually use --------------------
// GET stock-requisition/pickup-history?staff=<id>&page=1&limit=50
// (Not built yet — DUMMY_PICKUP_HISTORY below stands in until it exists.)
// -------------------------------------------------------------------------

// The logged-in staff's details are stored as a JSON blob under the
// "staff" key in localStorage. Shape looks like:
// { _id, name, email, userType, access, isDelete, createdAt, ... }
const getStaffInfo = () => {
    try {
        const raw = localStorage.getItem("staff");
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

// "Billu" -> "B", "Jane Doe" -> "JD"
const getInitials = (name) => {
    if (!name) return "?";
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
};

const formatUserType = (userType) => {
    if (!userType) return "";
    return userType.toString().replace(/[-_]/g, " ").toLowerCase();
};

const formatQuantity = (quantity) => {
    const number = Number(quantity || 0);
    return number.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const STATUS_STYLES = {
    COMPLETED: { bg: "bg-[#E5F7F3]", text: "text-[#0A6B58]", dot: "#00A88C" },
    PARTIAL: { bg: "bg-[#FFF7E6]", text: "text-[#B26A00]", dot: "#E08A3C" },
};

// ---- DUMMY DATA ---------------------------------------------------------
// Stand-in for the real pickup-history endpoint. Swap fetchPickupHistory()
// below for a real axiosInstance.get(...) call once that API exists — the
// shape each entry needs is: { _id, indentNumber, storageLocationName,
// status, pickedAt, items: [{ name, quantity, unit }] }.
const DUMMY_PICKUP_HISTORY = [
    {
        _id: "ph_1042",
        indentNumber: "IND-2026-1042",
        storageLocationName: "Coffee Corner",
        status: "COMPLETED",
        pickedAt: "2026-07-29T10:32:00Z",
        items: [
            { name: "Arabica Coffee Beans", quantity: 12, unit: "kg" },
            { name: "Paper Cups 12oz", quantity: 300, unit: "pcs" },
            { name: "Sugar Sachets", quantity: 5, unit: "kg" },
        ],
    },
    {
        _id: "ph_1039",
        indentNumber: "IND-2026-1039",
        storageLocationName: "Main Warehouse",
        status: "PARTIAL",
        pickedAt: "2026-07-28T15:10:00Z",
        items: [
            { name: "Disposable Gloves", quantity: 40, unit: "pcs" },
            { name: "Trash Bags Large", quantity: 25, unit: "pcs" },
        ],
    },
    {
        _id: "ph_1031",
        indentNumber: "IND-2026-1031",
        storageLocationName: "Bakery Unit",
        status: "COMPLETED",
        pickedAt: "2026-07-27T09:05:00Z",
        items: [
            { name: "All Purpose Flour", quantity: 20, unit: "kg" },
            { name: "Butter Blocks", quantity: 8, unit: "kg" },
            { name: "Baking Trays", quantity: 6, unit: "pcs" },
            { name: "Parchment Paper Rolls", quantity: 3, unit: "pcs" },
        ],
    },
    {
        _id: "ph_1022",
        indentNumber: "IND-2026-1022",
        storageLocationName: "Coffee Corner",
        status: "COMPLETED",
        pickedAt: "2026-07-24T11:48:00Z",
        items: [
            { name: "Robusta Coffee Beans", quantity: 10, unit: "kg" },
            { name: "Milk Cartons 1L", quantity: 24, unit: "pcs" },
        ],
    },
    {
        _id: "ph_1015",
        indentNumber: "IND-2026-1015",
        storageLocationName: "Main Warehouse",
        status: "COMPLETED",
        pickedAt: "2026-07-21T14:22:00Z",
        items: [
            { name: "Cling Film Rolls", quantity: 15, unit: "pcs" },
            { name: "Aluminium Foil Rolls", quantity: 10, unit: "pcs" },
            { name: "Food Containers 500ml", quantity: 200, unit: "pcs" },
        ],
    },
    {
        _id: "ph_1008",
        indentNumber: "IND-2026-1008",
        storageLocationName: "Fruits Freeze Dried Unit",
        status: "COMPLETED",
        pickedAt: "2026-07-18T08:55:00Z",
        items: [
            { name: "Freeze Dried Strawberry", quantity: 6, unit: "kg" },
            { name: "Freeze Dried Mango", quantity: 6, unit: "kg" },
        ],
    },
];

export default function Profile() {
    const navigate = useNavigate();

    // const [staffInfo] = useState(() => getStaffInfo());
    const [history] = useState(() => DUMMY_PICKUP_HISTORY);
    const [searchText, setSearchText] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const dispatch = useDispatch();
    const staffInfo = useSelector(selectUser);

    const stats = useMemo(() => {
        const totalPickups = history.length;

        const totalItems = history.reduce(
            (sum, entry) => sum + (entry.items?.length || 0),
            0
        );

        const now = new Date();
        const thisMonthCount = history.filter((entry) => {
            const d = new Date(entry.pickedAt);
            return (
                d.getMonth() === now.getMonth() &&
                d.getFullYear() === now.getFullYear()
            );
        }).length;

        return { totalPickups, totalItems, thisMonthCount };
    }, [history]);

    const filteredHistory = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) return history;

        return history.filter((entry) => {
            const haystack = [
                entry.indentNumber,
                entry.storageLocationName,
                ...(entry.items || []).map((it) => it.name),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(query);
        });
    }, [history, searchText]);

    const toggleExpanded = (id) => {
        setExpanded((previous) => {
            const next = new Set(previous);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // const handleLogout = () => {
    //     try {
    //         localStorage.removeItem("staff");
    //     } catch (err) {
    //         // ignore — still proceed to navigate away
    //     }
    //     navigate("/");
    // };

    const handleLogout = () => {
        dispatch(logout());                    // resets Redux, then store.js clears storage
        navigate("/", { replace: true });
    };

    return (
        <main className="min-h-screen flex justify-center xl-body" style={{ background: "#F4FAFC" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
                .xl-display { font-family: 'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }
            `}</style>

            <section className="relative w-full max-w-[430px] md:max-w-[680px] lg:max-w-[860px] min-h-screen bg-[#F4FAFC] shadow-[0_24px_70px_rgba(4,105,143,0.2)] overflow-hidden">
                {/* Header */}
                <div
                    className="sticky top-0 z-30 rounded-b-[28px] shadow-[0_8px_24px_rgba(4,105,143,0.18)]"
                    style={{ background: "linear-gradient(135deg, #04698F 0%, #00A4E3 100%)" }}
                >
                    <div className="flex items-center justify-between px-5 pt-4 pb-5">
                        <div className="min-w-0">
                            <h1 className="xl-display text-white text-[21px] font-bold leading-tight">
                                Profile
                            </h1>
                            <p className="xl-body text-white/80 text-xs font-medium mt-0.5">
                                Your profile details
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate('/home')}
                            aria-label="Go back"
                            className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white active:scale-95 transition shrink-0"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 14 4 9l5-5" />
                                <path d="M4 9h10.5A5.5 5.5 0 0 1 20 14.5v0A5.5 5.5 0 0 1 14.5 20H11" />
                            </svg>
                        </button>
                    </div>

                    {/* Waybill tear-strip — matches the Home screen's signature detail */}
                    <div className="relative h-0">
                        <div className="absolute left-8 right-8 top-0 border-t-2 border-dashed border-white/35" />
                        <div className="absolute -top-[7px] left-6 w-[14px] h-[14px] rounded-full bg-[#F4FAFC]" />
                        <div className="absolute -top-[7px] right-6 w-[14px] h-[14px] rounded-full bg-[#F4FAFC]" />
                    </div>
                </div>

                {/* Body */}
                <div className="px-5 pt-6 pb-10">
                    {/* Staff card */}
                    <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-5 shadow-[0_10px_30px_rgba(4,105,143,0.08)]">
                        <div className="flex items-center gap-4">
                            <div className="relative shrink-0">
                                <div
                                    className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl xl-display ring-4 ring-[#E8F7FF]"
                                    style={{ background: "linear-gradient(135deg, #00A4E3 0%, #04698F 100%)" }}
                                >
                                    {getInitials(staffInfo?.name)}
                                </div>
                                <span
                                    className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-[#00A88C] border-2 border-white"
                                    aria-hidden="true"
                                />
                            </div>

                            <div className="min-w-0 flex-1">
                                <p className="xl-display text-[#08213D] font-bold text-lg leading-tight truncate">
                                    {staffInfo?.name || "Unknown user"}
                                </p>

                                {staffInfo?.email && (
                                    <p className="xl-body text-[13px] font-medium text-[#5B6B7C] truncate mt-0.5">
                                        {staffInfo.email}
                                    </p>
                                )}

                                {staffInfo?.userType && (
                                    <span className="xl-mono inline-block mt-2 rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[9px] font-semibold text-[#5B6B7C] uppercase tracking-wide">
                                        {formatUserType(staffInfo.userType)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* {staffInfo?._id && (
                            <div className="mt-4 pt-4 border-t border-dashed border-[#DCEAF1] flex items-center justify-between">
                                <p className="xl-mono text-[9px] font-medium text-[#5B6B7C] uppercase tracking-wide">
                                    Staff ID
                                </p>
                                <p className="xl-mono text-[11px] font-semibold text-[#08213D] truncate">
                                    {staffInfo._id}
                                </p>
                            </div>
                        )} */}
                    </div>

                    {/* Account options */}
                    <div className="mt-4 rounded-[24px] bg-white border border-[#DCEAF1] p-2 shadow-[0_10px_30px_rgba(4,105,143,0.06)]">
                        <p className="xl-mono px-3 pt-2 pb-1 text-[9px] font-semibold text-[#9FB4BD] uppercase tracking-wide">
                            Account
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate('/privacy-policy')}
                            className="w-full inline-flex items-center gap-3 rounded-[16px] px-3 py-3.5 text-left transition hover:bg-[#F1FAFD] active:scale-[0.99]"
                        >
                            <span
                                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                                style={{ background: "#E8F7FF" }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#04698F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                                </svg>
                            </span>

                            <span className="flex-1 min-w-0">
                                <span className="block xl-body text-sm font-semibold text-[#08213D]">
                                    Privacy Policy
                                </span>
                                <span className="block xl-body text-[11px] font-medium text-[#5B6B7C] mt-0.5">
                                    How your data is handled
                                </span>
                            </span>

                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9FB4BD" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                <path d="m9 6 6 6-6 6" />
                            </svg>
                        </button>

                        <div className="mx-3 border-t border-dashed border-[#E4F2F7]" />

                        <button
                            type="button"
                            onClick={() => setShowLogoutConfirm(true)}
                            className="w-full inline-flex items-center gap-3 rounded-[16px] px-3 py-3.5 text-left transition hover:bg-[#FDECEC] active:scale-[0.99]"
                        >
                            <span
                                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                                style={{ background: "#FDECEC" }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <path d="M16 17l5-5-5-5" />
                                    <path d="M21 12H9" />
                                </svg>
                            </span>

                            <span className="flex-1 min-w-0">
                                <span className="block xl-body text-sm font-semibold text-[#D64545]">
                                    Logout
                                </span>
                                <span className="block xl-body text-[11px] font-medium text-[#5B6B7C] mt-0.5">
                                    Sign out of this device
                                </span>
                            </span>

                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D64545" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                <path d="m9 6 6 6-6 6" />
                            </svg>
                        </button>
                    </div>

                    {/* Stats row */}
                    {/* <div className="mt-4 rounded-[20px] bg-white border border-[#DCEAF1] px-4 py-3 flex items-center justify-between">
                        <div className="text-center flex-1">
                            <p className="xl-display text-[20px] font-bold text-[#08213D] leading-none">
                                {stats.totalPickups}
                            </p>
                            <p className="xl-mono text-[9px] font-medium text-[#5B6B7C] mt-1 uppercase tracking-wide">
                                Pickups
                            </p>
                        </div>

                        <div className="w-px h-8 bg-[#E4F2F7]" />

                        <div className="text-center flex-1">
                            <p className="xl-display text-[20px] font-bold text-[#08213D] leading-none">
                                {stats.totalItems}
                            </p>
                            <p className="xl-mono text-[9px] font-medium text-[#5B6B7C] mt-1 uppercase tracking-wide">
                                Items picked
                            </p>
                        </div>

                        <div className="w-px h-8 bg-[#E4F2F7]" />

                        <div className="text-center flex-1">
                            <p className="xl-display text-[20px] font-bold text-[#00A4E3] leading-none">
                                {stats.thisMonthCount}
                            </p>
                            <p className="xl-mono text-[9px] font-medium text-[#5B6B7C] mt-1 uppercase tracking-wide">
                                This month
                            </p>
                        </div>
                    </div> */}

                    {/* Pickup history */}
                    {/* <div className="mt-7"> */}
                    {/* <div className="flex items-center justify-between mb-3">
                            <h2 className="xl-display text-[#08213D] font-bold text-base leading-tight">
                                Pickup history
                            </h2>
                            <span className="xl-mono rounded-full bg-white border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-medium text-[#08213D]">
                                {history.length}
                            </span>
                        </div> */}

                    {/* <div className="relative mb-4">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00A4E3]">
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m21 21-4.3-4.3" />
                                </svg>
                            </span>

                            <input
                                type="search"
                                inputMode="search"
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                placeholder="Search indent no., location, or item"
                                className="xl-body w-full h-12 rounded-[16px] bg-white border border-[#DCEAF1] pl-11 pr-10 text-sm font-medium text-[#08213D] placeholder:text-[#9FB4BD] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30"
                            />

                            {searchText && (
                                <button
                                    type="button"
                                    onClick={() => setSearchText("")}
                                    aria-label="Clear search"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#F1FAFD] text-[#5B6B7C] flex items-center justify-center"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                </button>
                            )}
                        </div> */}

                    {/* {filteredHistory.length === 0 ? (
                            <div className="rounded-[24px] bg-white border border-[#DCEAF1] p-8 text-center">
                                <p className="xl-body text-sm font-semibold text-[#08213D]">
                                    No pickups match &quot;{searchText}&quot;
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSearchText("")}
                                    className="xl-body mt-4 px-6 h-11 rounded-[14px] text-white text-xs font-semibold active:scale-95 transition"
                                    style={{ background: "#00A4E3" }}
                                >
                                    Clear search
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredHistory.map((entry) => {
                                    const isOpen = expanded.has(entry._id);
                                    const items = entry.items || [];
                                    const statusStyle =
                                        STATUS_STYLES[entry.status] || STATUS_STYLES.COMPLETED;

                                    return (
                                        <div
                                            key={entry._id}
                                            className="rounded-[22px] bg-white border border-[#DCEAF1] overflow-hidden"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => toggleExpanded(entry._id)}
                                                className="w-full flex items-center justify-between gap-3 px-4 py-4 active:opacity-70 transition"
                                            >
                                                <div className="min-w-0 text-left">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="xl-mono text-[13px] font-bold text-[#00A4E3]">
                                                            {entry.indentNumber}
                                                        </span>

                                                        <span
                                                            className={`xl-body rounded-full ${statusStyle.bg} px-2.5 py-0.5 text-[10px] font-bold ${statusStyle.text} flex items-center gap-1`}
                                                        >
                                                            <span
                                                                className="w-1.5 h-1.5 rounded-full"
                                                                style={{ backgroundColor: statusStyle.dot }}
                                                            />
                                                            {entry.status === "COMPLETED" ? "Completed" : "Partial"}
                                                        </span>
                                                    </div>

                                                    <p className="xl-body text-xs font-medium text-[#5B6B7C] mt-1.5 truncate">
                                                        {entry.storageLocationName}
                                                        {" · "}
                                                        {formatDate(entry.pickedAt)}
                                                        {" · "}
                                                        {formatTime(entry.pickedAt)}
                                                    </p>
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className="xl-body rounded-full bg-[#F1FAFD] border border-[#DCEAF1] px-2.5 py-0.5 text-[10px] font-semibold text-[#5B6B7C]">
                                                        {items.length} item{items.length === 1 ? "" : "s"}
                                                    </span>

                                                    <svg
                                                        width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6B7C" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"
                                                        className={`transition-transform ${isOpen ? "" : "-rotate-90"}`}
                                                    >
                                                        <path d="m6 9 6 6 6-6" />
                                                    </svg>
                                                </div>
                                            </button>

                                            {isOpen && (
                                                <div className="border-t border-dashed border-[#DCEAF1] px-4 py-3">
                                                    {items.length === 0 ? (
                                                        <p className="xl-body text-xs font-medium text-[#5B6B7C] py-2">
                                                            No items on this pickup
                                                        </p>
                                                    ) : (
                                                        items.map((item, idx) => (
                                                            <div
                                                                key={`${entry._id}-${idx}`}
                                                                className={`flex items-center justify-between gap-3 py-2 ${idx < items.length - 1 ? "border-b border-[#F1FAFD]" : ""
                                                                    }`}
                                                            >
                                                                <p className="xl-body text-[13px] font-semibold text-[#08213D] truncate">
                                                                    {item.name}
                                                                </p>
                                                                <p className="xl-display text-[14px] font-bold text-[#08213D] shrink-0 whitespace-nowrap">
                                                                    {formatQuantity(item.quantity)}
                                                                    <span className="xl-body text-[11px] font-semibold text-[#5B6B7C] ml-1">
                                                                        {item.unit}
                                                                    </span>
                                                                </p>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )} */}
                    {/* </div> */}
                </div>

                {/* Logout confirmation sheet */}
                {showLogoutConfirm && (
                    <div className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-black/40 px-5">
                        <div className="w-full max-w-[380px] rounded-[24px] bg-white p-5 mb-6 md:mb-0 shadow-[0_24px_70px_rgba(4,105,143,0.25)]">
                            <div className="w-11 h-11 rounded-full bg-[#FDECEC] flex items-center justify-center mb-3">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <path d="M16 17l5-5-5-5" />
                                    <path d="M21 12H9" />
                                </svg>
                            </div>

                            <p className="xl-display text-[#08213D] font-bold text-base">
                                Log out of this device?
                            </p>
                            <p className="xl-body text-[13px] font-medium text-[#5B6B7C] mt-1">
                                You'll need to sign in again to continue.
                            </p>

                            <div className="flex items-center gap-3 mt-5">
                                <button
                                    type="button"
                                    onClick={() => setShowLogoutConfirm(false)}
                                    className="xl-body flex-1 h-11 rounded-[14px] border border-[#DCEAF1] text-sm font-semibold text-[#08213D] active:scale-95 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="xl-body flex-1 h-11 rounded-[14px] text-white text-sm font-semibold active:scale-95 transition"
                                    style={{ background: "#D64545" }}
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </main>
    );
}